/** RFC 0267 — instant-app registration write model. */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { createProjectOwnedDocument } from '../project/creation'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import {
  cleanIconSpec,
  generateIcon,
  type IconSpec,
  iconSpecError,
} from './app-icon'
import { readSlotFills } from './reads'
import type {
  AppCapabilities,
  AppDoc,
  AppPresentation,
  AppSchemaSpec,
  Layout,
  SettingSpec,
  SettingValue,
  Slot,
  SlotFill,
  SlotGeometry,
} from './types'

/**
 * A layout's own document (RFC 0284 §4e).
 *
 * Deliberately not an `AppDoc`: no schema, no capabilities, no view, no skill,
 * because a layout is read and never run. It carries identity and the record,
 * and its id is what the catalog entry is keyed by.
 */
interface LayoutDoc {
  id: string
  name: string
  description: string
  presentation: 'layout'
  layout: Layout
  createdAt: number
  updatedAt: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * Register an instant app (RFC 0267): create the app's own Automerge document
 * (the authoritative full record) and a thin catalog entry in the project's
 * `apps` map, keyed by that document's id. The view and skill are documents the
 * AGENT already authored (via createDocument); this ties them to a data type
 * and the granted capabilities. Returns the appId (the app document's id).
 *
 * The app is its own document (not a synthetic-uuid inline blob), matching how
 * sessions and documents are modeled: the id is a real, addressable doc id, and
 * the doc can carry version history / graduation state later. Identity survives
 * a rename or a swapped view doc (searugast's stable-id rationale), because the
 * id is the doc's, independent of name and links. `writes` defaults to the
 * app's own data type so the common case needs no capability boilerplate.
 */
export async function registerApp(
  repo: Repo,
  projectId: string,
  opts: {
    name: string
    description: string
    dataType: string
    schema: AppSchemaSpec
    skillDocId: string
    viewDocId: string
    /** RFC 0274: the `SourceManifest` document, when this app was built. */
    sourceDocId?: string
    runtime?: string
    capabilities?: Partial<AppCapabilities>
    workerName?: string
    /** Sections this app adds to the canvas side column (RFC 0273). */
    panels?: SlotFill[]
    /** RFC 0271: "document" lists instances beside other documents. */
    presentation?: AppPresentation
    /** List and open this app's instances on their own, while keeping its own
     *  index. Independent of `presentation`; see `AppRef.instancesOpenable`. */
    instancesOpenable?: boolean
    /** RFC 0275 §4: mime types this app EDITS. At most one app per type. */
    handles?: string[]
    /** RFC 0275 §3: "host" runs in the host realm and can hold a handle. */
    realm?: 'host'
    /** RFC 0275: opt in to real handles over an isolated repo. Costs ~4MB in
     *  the frame, so it is declared rather than assumed. */
    sync?: 'documents'
    /** The app's icon (RFC 0332). Refused here if it is not a valid spec. */
    icon?: IconSpec
    /**
     * Where the catalog ENTRY goes (RFC 0295 §4a). The World's id installs the
     * app for every project on the deployment; absent, the project's own.
     * The app's documents stay in `workspaceId` either way: a World has no
     * document catalog, and an editor is one app whichever project opens it.
     */
    catalogId?: string
  },
): Promise<string> {
  // Validated here, whoever calls: a pushed package.json or a tool, so a
  // malformed fill never reaches the catalog every window reads.
  const panels = readSlotFills(opts.panels)
  const now = Date.now()
  const catalogId = opts.catalogId ?? projectId
  // Refused where it is written, so a bad icon never reaches a surface.
  const iconError = iconSpecError(opts.icon)
  if (iconError) throw new Error(iconError)

  /**
   * ONE APP PER DATA TYPE, refused here rather than raced later.
   *
   * A document is resolved to the app that draws it by TYPE, with `find`, in
   * more than one place (the project shell's file resolution, the panel
   * column, `mod app push`). `find` returns the first match, so a second app
   * claiming a type does not produce an error, it produces a coin flip decided
   * by map order: two apps owning the same records, and which one renders an
   * instance changing for reasons nobody can see.
   *
   * The rule belonged here all along. It was enforced in `mod app push` first,
   * which left every other door open, including `register_app` in the agent
   * tool catalogue: an agent asked twice for "a poll app" would make a second
   * one and quietly break the first.
   */
  const wsHandle = await repo.find<ProjectDoc>(catalogId as AnyDocumentId)
  await (wsHandle as { whenReady?: () => Promise<void> }).whenReady?.()
  const apps = (wsHandle.doc()?.apps ?? {}) as Record<
    string,
    { name?: string; dataType?: string; handles?: string[] }
  >
  for (const [id, ref] of Object.entries(apps)) {
    // An app that only `handles` a mime has no dataType, and two of those own
    // nothing in common: the text editor and the code viewer are both `""`
    // here, and refusing the second was the rule below applied to a type that
    // does not exist.
    if (opts.dataType && projectString(ref?.dataType) === opts.dataType) {
      throw new Error(
        `"${ref.name ?? id}" (${id}) already owns dataType "${opts.dataType}" ` +
          'in this project. Two apps on one type cannot both draw its ' +
          'records, and which one wins is decided by map order. Update that ' +
          'app, or give this one its own dataType.',
      )
    }
    /**
     * NO SUCH RULE FOR `handles` ANY MORE (RFC 0284 S6m).
     *
     * This used to refuse a second app claiming a mime, on the grounds that
     * resolution was a `find` and a second claim would be a coin flip. Both
     * halves were wrong. The refusal only checked the catalog being written, so
     * a World app and a project app could claim one type anyway and map order
     * still decided; and it made trying a different editor mean uninstalling
     * the one you had, which is what made an installed thread a one-way door.
     *
     * `resolveHandler` decides now, and its answer for several claimants and no
     * choice is the BUILT-IN rather than a coin flip. So several claimants is a
     * supported state, and picking between them is a person's to make.
     *
     * `dataType` above keeps its rule, because that is not a preference: two
     * apps owning one record type both write those records, and no choice a
     * person makes reconciles that.
     */
  }

  const writes = opts.capabilities?.writes ?? [opts.dataType]
  const reads = opts.capabilities?.reads ?? []
  const doc = await createProjectOwnedDocument<AppDoc>(repo, projectId)
  const appId = doc.documentId as string
  doc.change((d) => {
    d.id = appId
    d.workspaceId = projectId
    d.name = opts.name
    d.description = opts.description
    d.dataType = opts.dataType
    d.schema = opts.schema
    d.skillDocId = opts.skillDocId
    d.viewDocId = opts.viewDocId
    if (opts.sourceDocId) d.sourceDocId = opts.sourceDocId
    d.runtime = opts.runtime ?? 'html'
    d.capabilities = { writes, reads }
    if (panels.length) d.panels = panels
    // Omitted when "application": absent already means that, and writing the
    // default would make every app doc claim a field it never chose.
    if (opts.presentation === 'document') d.presentation = 'document'
    // Same rule, same reason: absent is the default, so only `true` is written.
    if (opts.instancesOpenable) d.instancesOpenable = true
    if (opts.handles?.length) d.handles = opts.handles
    if (opts.realm === 'host') d.realm = 'host'
    if (opts.sync === 'documents') d.sync = 'documents'
    d.tier = 'instant'
    d.createdAt = now
    d.updatedAt = now
    if (opts.workerName) d.workerName = opts.workerName
  })
  const project = await repo.find<ProjectDoc>(catalogId as AnyDocumentId)
  changeProject(project, (d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[appId] = {
      name: opts.name,
      description: opts.description,
      dataType: opts.dataType,
      viewDocId: opts.viewDocId,
      skillDocId: opts.skillDocId,
      updatedAt: now,
      // Omitted when absent rather than written as []: an app that declares no
      // section should not carry an empty array into every catalog read.
      ...(panels.length ? { panels } : {}),
      ...(opts.presentation === 'document'
        ? { presentation: 'document' as const }
        : {}),
      ...(opts.instancesOpenable ? { instancesOpenable: true } : {}),
      ...(opts.handles?.length ? { handles: opts.handles } : {}),
      ...(opts.realm === 'host' ? { realm: 'host' as const } : {}),
      ...(opts.sync === 'documents' ? { sync: 'documents' as const } : {}),
      // Generated once and stored (RFC 0332 3e): what the app set in
      // `mod.icon`, with the rest filled in from what it is. Every surface
      // then draws this, rather than working an icon out on every draw.
      icon: cleanIconSpec(
        generateIcon(
          {
            id: appId,
            name: opts.name,
            dataType: opts.dataType,
            description: opts.description,
          },
          opts.icon,
        ),
      ),
    }
  })
  return appId
}

/**
 * Update a registered app IN PLACE (RFC 0267), keeping its id.
 *
 * `registerApp` always mints a new app doc, so the only way to correct a
 * registration was unregister + re-register — which changes the app id, runs a
 * full capability release/resolve cycle, and briefly leaves the project with
 * either two apps on one dataType or none. That is far too much ceremony for
 * "the schema drifted from the view", which is the common case: an app's view
 * and skill evolve, and the registration silently stops describing them.
 *
 * Only the fields passed are touched. `dataType` is deliberately NOT updatable:
 * it is the app's identity for its instances, and changing it would orphan
 * every existing document of the old type.
 */
export async function updateApp(
  repo: Repo,
  projectId: string,
  appId: string,
  opts: {
    name?: string
    description?: string
    schema?: AppSchemaSpec
    skillDocId?: string
    viewDocId?: string
    /** RFC 0274: the `SourceManifest` document. Written once by the first
     *  `mod app push`; re-pushes reuse it rather than orphaning a manifest. */
    sourceDocId?: string
    runtime?: string
    capabilities?: Partial<AppCapabilities>
    /** `null` DELETES it; absent leaves it as it is. */
    workerName?: string | null
    /** Sections in the canvas side column (RFC 0273). `[]` removes them all,
     *  which is how an app stops contributing without being unregistered. */
    panels?: SlotFill[]
    /** RFC 0271. Setting "application" DELETES the field rather than writing
     *  the default, so "never chose" and "chose the default" stay identical. */
    presentation?: AppPresentation
    /** List and open this app's instances on their own. `false` DELETES the
     *  field, for `presentation`'s reason: absent is the default, and a stored
     *  `false` would say the app chose what it merely never asked about. */
    instancesOpenable?: boolean
    /** RFC 0275 §4: mime types this app edits. Same uniqueness rule as
     *  registration; changing them changes which app opens a document. */
    handles?: string[]
    /** RFC 0275 §3: where it runs. `null` DELETES it (back to sandboxed);
     *  absent leaves it as it is. */
    realm?: 'host' | null
    /** RFC 0275: whether it gets real handles, and the frame cost that implies.
     *  `null` DELETES it; absent leaves it as it is. */
    sync?: 'documents' | null
    /** The app's icon (RFC 0332). `null` DELETES it, so the icon goes back to
     *  generated; absent leaves it as it is. */
    icon?: IconSpec | null
    /** The catalog holding the entry, when it is not the project's (RFC 0295
     *  §4a): a World-installed app is updated in the World. */
    catalogId?: string
  },
): Promise<void> {
  const panels =
    opts.panels === undefined ? undefined : readSlotFills(opts.panels)
  const now = Date.now()
  if (opts.icon) {
    const iconError = iconSpecError(opts.icon)
    if (iconError) throw new Error(iconError)
  }
  const handle = await repo.find<AppDoc>(appId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const before = handle.doc() as AppDoc | undefined
  if (!before) throw new Error(`app ${appId} not found`)

  // Capability READS drive install refcounts (RFC 0270 §4), so a change to them
  // must go through the same resolve/release cycle registration uses — not a
  // plain field write, which would leave a capability installed for an app that
  // no longer declares it, or declared by an app that never installed it.
  const nextReads = opts.capabilities?.reads
  if (nextReads) {
    const prevReads = before.capabilities?.reads ?? []
    const added = nextReads.filter((r) => !prevReads.includes(r))
    const removed = prevReads.filter((r) => !nextReads.includes(r))
    if (added.length > 0) {
    }
    if (removed.length > 0) {
      // Re-resolve what it still declares: release drops every grant this app
      // held, so the ones it keeps must be re-taken.
      const kept = nextReads.filter((r) => prevReads.includes(r))
      if (kept.length > 0) {
      }
    }
  }

  handle.change((d) => {
    if (opts.name !== undefined) d.name = opts.name
    if (opts.description !== undefined) d.description = opts.description
    if (opts.schema !== undefined) d.schema = opts.schema
    if (opts.skillDocId !== undefined) d.skillDocId = opts.skillDocId
    if (opts.viewDocId !== undefined) d.viewDocId = opts.viewDocId
    if (opts.sourceDocId !== undefined) d.sourceDocId = opts.sourceDocId
    if (opts.handles !== undefined) {
      if (opts.handles.length > 0) d.handles = opts.handles
      else delete d.handles
    }
    if (opts.realm !== undefined) {
      if (opts.realm === 'host') d.realm = 'host'
      else delete d.realm
    }
    if (opts.sync !== undefined) {
      if (opts.sync === 'documents') d.sync = 'documents'
      else delete d.sync
    }
    if (opts.runtime !== undefined) d.runtime = opts.runtime
    if (opts.workerName !== undefined) {
      if (opts.workerName) d.workerName = opts.workerName
      else delete d.workerName
    }
    if (opts.presentation !== undefined) {
      if (opts.presentation === 'document') d.presentation = 'document'
      else delete d.presentation
    }
    if (opts.instancesOpenable !== undefined) {
      if (opts.instancesOpenable) d.instancesOpenable = true
      else delete d.instancesOpenable
    }
    if (panels !== undefined) {
      // Same rule as the catalog copy: emptied means removed, not stored as [].
      if (panels.length > 0) d.panels = panels
      else delete d.panels
    }
    if (opts.capabilities !== undefined) {
      d.capabilities = {
        writes: opts.capabilities.writes ?? d.capabilities?.writes ?? [],
        reads: opts.capabilities.reads ?? d.capabilities?.reads ?? [],
      }
    }
    d.updatedAt = now
  })

  // The project catalog carries a THIN copy for zero-hydration rendering, so
  // it has to follow or the sidebar keeps showing the old name.
  const project = await repo.find<ProjectDoc>(
    (opts.catalogId ?? projectId) as AnyDocumentId,
  )
  changeProject(project, (d: Draft) => {
    const ref = d.apps?.[appId]
    if (!ref) return
    if (panels !== undefined) {
      // Deleted rather than set to [] when emptied, so "no sections" reads the
      // same whether an app never declared any or dropped the ones it had.
      if (panels.length > 0) ref.panels = panels
      else delete ref.panels
    }
    if (opts.presentation !== undefined) {
      if (opts.presentation === 'document') ref.presentation = 'document'
      else delete ref.presentation
    }
    // The catalog copy is the one that DECIDES: the sidebar and the pane
    // resolver both read it, and neither hydrates an app doc. Writing only the
    // app doc would leave a successful change with no visible effect at all.
    if (opts.instancesOpenable !== undefined) {
      if (opts.instancesOpenable) ref.instancesOpenable = true
      else delete ref.instancesOpenable
    }
    if (opts.name !== undefined) ref.name = opts.name
    if (opts.description !== undefined) ref.description = opts.description
    if (opts.viewDocId !== undefined) ref.viewDocId = opts.viewDocId
    if (opts.skillDocId !== undefined) ref.skillDocId = opts.skillDocId
    // RFC 0275. These three decide how the host MOUNTS an app — which editor a
    // document gets, whether a realm is built, whether a sync port is
    // transferred — and the host reads them off the catalog, before it hydrates
    // anything. Omitting them here meant an app could declare `sync` in its
    // manifest, have it written to its own document, and still be mounted
    // without a port: the declaration was true everywhere except the one place
    // that is read. Apps registered fresh worked and apps UPDATED did not,
    // which is the worst version of this bug because it looks like it works.
    if (opts.handles !== undefined) {
      if (opts.handles.length > 0) ref.handles = opts.handles
      else delete ref.handles
    }
    if (opts.realm !== undefined) {
      if (opts.realm === 'host') ref.realm = 'host'
      else delete ref.realm
    }
    if (opts.sync !== undefined) {
      if (opts.sync === 'documents') ref.sync = 'documents'
      else delete ref.sync
    }
    // On the catalog only: it is the copy every row reads (RFC 0332 3b).
    if (opts.icon !== undefined) {
      if (opts.icon) ref.icon = cleanIconSpec(opts.icon)
      else delete ref.icon
    }
    ref.updatedAt = now
  })
}

/** Remove an app from the project catalog (RFC 0267). Registry unlink only;
 *  the app, view, and skill documents are left to reachability GC, and
 *  instances of its data type are untouched (they are ordinary documents). */
export async function unregisterApp(
  repo: Repo,
  projectId: string,
  appId: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  changeProject(project, (d: Draft) => {
    if (d.apps) delete d.apps[appId]
  })
}

/**
 * Move a catalog entry between catalogs, keeping the app's id (RFC 0295 §4a).
 *
 * The case is an app pushed into one project that should serve every project:
 * the entry is copied into the World and removed from the project. The id is
 * kept because instances, settings and a person's `handlers` choice all name
 * it; re-registering would mint a new one and orphan all three. The app doc
 * is untouched, since it says which project holds the documents, and that has
 * not changed.
 *
 * The copy lands BEFORE the delete, so a reader between the two writes sees
 * the app twice (and `installedApps` lets the project win) rather than not at
 * all. Nothing to move is not an error: the entry is already where it should be.
 */
export async function moveApp(
  repo: Repo,
  appId: string,
  from: string,
  to: string,
): Promise<boolean> {
  if (from === to) return false
  // BOTH HANDLES FIRST, then read and write with no await between. Reading the
  // entry before waiting on the target let an update land in the gap, and the
  // move then wrote the old copy into the target and deleted the new one.
  const source = await repo.find<ProjectDoc>(from as AnyDocumentId)
  await (source as { whenReady?: () => Promise<void> }).whenReady?.()
  const target = await repo.find<ProjectDoc>(to as AnyDocumentId)
  await (target as { whenReady?: () => Promise<void> }).whenReady?.()
  const entry = source.doc()?.apps?.[appId]
  if (!entry) return false
  const copy = JSON.parse(JSON.stringify(entry))
  changeProject(target, (d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[appId] = copy
  })
  changeProject(source, (d: Draft) => {
    if (d.apps) delete d.apps[appId]
  })
  return true
}

/**
 * Install a layout (RFC 0284 §4e, RFC 0295 S3).
 *
 * NOT `registerApp`, which demands a schema, a view document, a skill document
 * and a data type. A layout has none of those, and passing empty strings for
 * four required fields to reach one write is how a shape stops meaning
 * anything. What they share is the catalog entry, which is the part that
 * matters: a layout is listed, installed, switched to and uninstalled by the
 * same machinery as everything else.
 *
 * THE DOCUMENT EXISTS SO THE ID DOES. A catalog is keyed by an app document's
 * id (RFC 0267), and inventing a key for layouts alone would give one kind of
 * entry an id space nobody else uses. The document holds the layout too, which
 * is what makes editing one an ordinary document edit rather than a write into
 * the directory every client opens first.
 *
 * THE SLOTS ARE ON THE ENTRY AS WELL, deliberately duplicated. The shell needs
 * them before it has hydrated anything, or the first frame draws no chrome
 * while a document loads. `setLayoutSlots` writes both, and the entry is the
 * copy that is read.
 */
export async function installLayout(
  repo: Repo,
  catalogId: string,
  opts: {
    name: string
    description?: string
    /** Slot to fill ids. An empty array EMPTIES that slot; a slot left
     *  out is unspecified and falls back to the built-in. */
    slots: Partial<Record<Slot, string[]>>
    /** How each slot is shaped (RFC 0284 S6d). Omitted leaves every surface at
     *  the shell's default, which is what a layout written before this had. */
    geometry?: Partial<Record<Slot, SlotGeometry>>
    /** The panes a space using it opens on (RFC 0284 S6h), encoded. */
    tree?: string
  },
): Promise<string> {
  const now = Date.now()
  const layout: Layout = {
    slots: opts.slots,
    // Omitted rather than written as {}, so a layout that says nothing about
    // shape carries no key claiming it did.
    ...(opts.geometry ? { geometry: opts.geometry } : {}),
    ...(opts.tree ? { tree: opts.tree } : {}),
  }
  const doc = repo.create<LayoutDoc>()
  const layoutId = doc.documentId as string
  doc.change((d) => {
    d.id = layoutId
    d.name = opts.name
    d.description = opts.description ?? ''
    d.presentation = 'layout'
    d.layout = layout
    d.createdAt = now
    d.updatedAt = now
  })
  const catalog = await repo.find(catalogId as AnyDocumentId)
  changeProject(catalog, (d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[layoutId] = {
      name: opts.name,
      description: opts.description ?? '',
      // A layout mints no records, so it owns no type. Empty rather than
      // absent because `AppRef.dataType` is required, and the one-app-per-type
      // rule keys on it: several layouts would otherwise all "own" undefined.
      dataType: '',
      presentation: 'layout' as const,
      layout,
      updatedAt: now,
    }
  })
  return layoutId
}

/**
 * Change what a layout puts in its slots.
 *
 * Writes the entry and the document, in that order, because the entry is what
 * is read. A caller that failed between the two leaves the document stale and
 * the window correct, which is the harmless direction.
 *
 * WHOLE-SLOTS REPLACEMENT, not a per-slot merge. Two devices editing one layout
 * concurrently would otherwise merge into an arrangement neither chose, and the
 * spaces document already made this decision for the tree (RFC 0281 §7).
 */
export async function setLayoutSlots(
  repo: Repo,
  catalogId: string,
  layoutId: string,
  slots: Partial<Record<Slot, string[]>>,
): Promise<void> {
  /**
   * GEOMETRY IS CARRIED, not overwritten.
   *
   * This wrote `{ slots }` wholesale, which was correct while `slots` was the
   * whole of a layout and became a data-loss bug the moment `geometry` joined
   * it: changing the sidebar's fill would have silently unhidden a header
   * somebody had set to reveal on hover. `setLayoutGeometry` does the mirror
   * image for the same reason.
   */
  await writeLayout(repo, catalogId, layoutId, (prev) => ({
    ...prev,
    slots,
  }))
}

/**
 * Change how a layout SHAPES its slots (RFC 0284 S6d).
 *
 * WHOLESALE per call, like `setLayoutSlots`: the caller sends the whole
 * geometry map. A merge here would make removing a slot's geometry impossible
 * to express, and "back to the default" is the state this most needs to reach.
 */
export async function setLayoutGeometry(
  repo: Repo,
  catalogId: string,
  layoutId: string,
  geometry: Partial<Record<Slot, SlotGeometry>>,
): Promise<void> {
  await writeLayout(repo, catalogId, layoutId, (prev) => ({
    // EVERYTHING ELSE CARRIED. Spelling `{ slots, geometry }` here dropped
    // `tree` the moment S6h added it, so shaping the header silently threw away
    // the panes a layout opened on. Spread first, then overwrite the one field
    // this writer owns, and the next field added to `Layout` survives by
    // default rather than by somebody remembering.
    ...prev,
    slots: prev?.slots ?? {},
    geometry,
  }))
}

/**
 * Set the panes a layout opens a space on (RFC 0284 S6h).
 *
 * `undefined` CLEARS, which is how a layout goes back to opening on the picker.
 * Storing an empty string instead would decode to nothing and render a blank
 * window, which is not a state anybody asked for.
 *
 * Carries the slots and geometry across, like its two siblings: this is the
 * third writer on one structure, and the third chance to silently erase the
 * other two.
 */
export async function setLayoutTree(
  repo: Repo,
  catalogId: string,
  layoutId: string,
  tree: string | undefined,
): Promise<void> {
  await writeLayout(repo, catalogId, layoutId, (prev) => {
    const next: Layout = { ...prev, slots: prev?.slots ?? {} }
    // DELETED rather than set to '', so `layoutTree` reads it as "no panes"
    // through the absent path rather than the junk-value one.
    if (tree) next.tree = tree
    else delete next.tree
    return next
  })
}

/**
 * The catalog entry and the layout's own document, in that order.
 *
 * The entry first because it is what the shell reads: a layout whose document
 * updated and whose entry did not is a layout nobody sees change.
 */
async function writeLayout(
  repo: Repo,
  catalogId: string,
  layoutId: string,
  next: (prev: Layout | undefined) => Layout,
): Promise<void> {
  const now = Date.now()

  const catalog = await repo.find(catalogId as AnyDocumentId)
  const catalogPrev = plain(
    (catalog.doc() as Draft)?.apps?.[layoutId]?.layout as Layout | undefined,
  )
  const catalogNext = plain(next(catalogPrev))
  changeProject(catalog, (d: Draft) => {
    const ref = d.apps?.[layoutId]
    if (!ref) return
    ref.layout = catalogNext
    ref.updatedAt = now
  })

  const doc = await repo.find(layoutId as AnyDocumentId)
  const docPrev = plain((doc.doc() as Draft)?.layout as Layout | undefined)
  const docNext = plain(next(docPrev))
  doc.change((d: Draft) => {
    d.layout = docNext
    d.updatedAt = now
  })
}

/**
 * A PLAIN copy, read OUT of the document before it is written back IN.
 *
 * Automerge refuses to store a value that is already part of a document
 * ("Cannot create a reference to an existing document object"), and a carry-
 * forward write is exactly that: the previous layout is read from the doc,
 * spread, and assigned. Spreading is not enough, because the nested `slots` and
 * `geometry` objects come through by reference. Cloned on the way out and again
 * on the way in, so the two documents never share one object either.
 */
function plain<T>(value: T | undefined): T | undefined {
  return value === undefined
    ? undefined
    : (JSON.parse(JSON.stringify(value)) as T)
}

/**
 * Install a chrome fill (RFC 0284 S5).
 *
 * The authoring path for the two surfaces this RFC exists to open up: a
 * sidebar, and anything else that fills a slot. An agent writes a module and
 * calls this, and the module is chrome from the next frame.
 *
 * A VIEW DOCUMENT HOLDS THE CODE, like every other app's. The shell reads it
 * and mounts it in the host realm, so a fill can hold a live handle and
 * follow the working set. That is the whole reason chrome is host-realm and not
 * sandboxed: a sidebar that only sees JSON snapshots is a sidebar that is
 * always slightly behind.
 *
 * THE CONTRIBUTION ID IS WHAT A LAYOUT NAMES, not the app id, matching how the
 * built-in is named `mod.sidebar`. One app can therefore fill more than one
 * slot, and a layout naming a fill means the same thing whether it was
 * bundled or installed.
 *
 * NOT `registerApp`, for the same reason `installLayout` is not: a fill
 * mints no records, so it has no schema, no data type, and no instances. What
 * it shares is the catalog entry, which is the part that matters.
 */
export async function installChrome(
  repo: Repo,
  catalogId: string,
  opts: {
    name: string
    description?: string
    /** Which surface it fills. */
    slot: Slot
    /** What a layout names it by. Defaults to the app's own id, which is what
     *  a caller who only ever fills one slot should not have to think about. */
    fillId?: string
    /** The module source. Exports default a mount function taking a container
     *  and `mod.window`. */
    code: string
    /** What it can be configured to do (RFC 0284 S6g). Declared here so a
     *  person can change it in Settings without the module being rewritten. */
    settings?: SettingSpec[]
  },
): Promise<{ appId: string; fillId: string }> {
  const now = Date.now()

  // The code, in its own document, so editing chrome is editing a document and
  // the shell can watch it change rather than needing a reinstall.
  const view = repo.create<{ content: string }>()
  view.change((d) => {
    d.content = opts.code
  })
  const viewDocId = view.documentId as string

  const doc = repo.create<Record<string, unknown>>()
  const appId = doc.documentId as string
  const fillId = opts.fillId ?? appId
  const fill = {
    id: fillId,
    title: opts.name,
    slot: opts.slot,
    kind: 'view' as const,
  }
  doc.change((d) => {
    d.id = appId
    d.name = opts.name
    d.description = opts.description ?? ''
    d.viewDocId = viewDocId
    d.realm = 'host'
    d.panels = [fill]
    if (opts.settings?.length) d.settings = opts.settings
    d.createdAt = now
    d.updatedAt = now
  })

  const catalog = await repo.find(catalogId as AnyDocumentId)
  changeProject(catalog, (d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[appId] = {
      name: opts.name,
      description: opts.description ?? '',
      // Mints no records, so it owns no type. See `installLayout` on why this
      // is empty rather than absent.
      dataType: '',
      viewDocId,
      // HOST REALM, always. The shell refuses to mount a sandboxed
      // fill, so writing anything else here installs something that
      // can never draw.
      realm: 'host' as const,
      panels: [fill],
      // Omitted rather than [] when an app declares none, so a catalog entry
      // does not claim a settings surface it has nothing to put in.
      ...(opts.settings?.length ? { settings: opts.settings } : {}),
      updatedAt: now,
    }
  })
  return { appId, fillId }
}

/**
 * Install a LEAF RENDERER: a module that draws a session (RFC 0284 S6b).
 *
 * The sibling of `installChrome`, and separate because the two answer
 * different questions. A fill fills a named chrome SURFACE and a layout
 * decides whether it does; a renderer claims a content KIND and is resolved by
 * `handles`, the same lookup a markdown file uses to find its editor. Folding
 * them together would give a session leaf a resolution path no document leaf
 * has, which is the asymmetry §4b exists to remove.
 *
 * NO LAYOUT IS TOUCHED, and that is the visible consequence. Installing one of
 * these takes effect at once, and going back to the shipped thread means
 * uninstalling it rather than choosing something else in Settings. That is how
 * an installed editor already behaves.
 */
export async function installRenderer(
  repo: Repo,
  catalogId: string,
  opts: {
    name: string
    description?: string
    /** What it draws. `'agent'` is a session; a mime is a document. */
    handles: string
    /** The module source. Exports default a mount function taking a container,
     *  `mod.window` and the session it is drawing. */
    code: string
  },
): Promise<{ appId: string }> {
  const now = Date.now()

  const view = repo.create<{ content: string }>()
  view.change((d) => {
    d.content = opts.code
  })
  const viewDocId = view.documentId as string

  const doc = repo.create<Record<string, unknown>>()
  const appId = doc.documentId as string
  doc.change((d) => {
    d.id = appId
    d.name = opts.name
    d.description = opts.description ?? ''
    d.viewDocId = viewDocId
    d.realm = 'host'
    d.handles = [opts.handles]
    d.createdAt = now
    d.updatedAt = now
  })

  const catalog = await repo.find(catalogId as AnyDocumentId)
  changeProject(catalog, (d: Draft) => {
    if (!d.apps) d.apps = {}
    d.apps[appId] = {
      name: opts.name,
      description: opts.description ?? '',
      dataType: '',
      viewDocId,
      // HOST REALM, always, for the reason a chrome fill is: a thread
      // has to hold a live handle on the session it draws, and the sandboxed
      // tier can carry a snapshot but not a subscription.
      realm: 'host' as const,
      handles: [opts.handles],
      updatedAt: now,
    }
  })
  return { appId }
}

/**
 * Set one of an app's settings (RFC 0284 S6g).
 *
 * ON THE CATALOG ENTRY, not the app document. Settings are read on a
 * fill's first frame, so putting them where reading costs a hydration
 * would undo the property the thin catalog exists for. The app document is the
 * authoritative record of what the app IS; what a person set it to is not that.
 *
 * `undefined` DELETES, which is how a setting goes back to its declared
 * default. Writing the default instead would leave the entry claiming a
 * decision nobody made, and would freeze it if the app later changed its mind
 * about what the default should be.
 */
export async function setAppSetting(
  repo: Repo,
  catalogId: string,
  appId: string,
  key: string,
  value: SettingValue | undefined,
): Promise<void> {
  const now = Date.now()
  const catalog = await repo.find(catalogId as AnyDocumentId)
  changeProject(catalog, (d: Draft) => {
    const ref = d.apps?.[appId]
    if (!ref) return
    if (value === undefined) {
      if (ref.settingValues) delete ref.settingValues[key]
      return
    }
    if (!ref.settingValues) ref.settingValues = {}
    ref.settingValues[key] = value
    ref.updatedAt = now
  })
}
